
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import axios from 'axios';
import { Container, Select, MenuItem, Button, Typography, Paper, CircularProgress, Alert, FormControl, InputLabel } from '@mui/material';

axios.defaults.withCredentials = true;

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
}

const GeneratePhotographerTokenPage = () => {
    const query = useQuery();
    const raceId = query.get('raceId');

    const [races, setRaces] = useState([]);
    const [selectedRace, setSelectedRace] = useState(raceId || '');
    const [token, setToken] = useState('');
    const [error, setError] = useState('');
    
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchUserAndRaces = async () => {
            try {
                const userStatus = await axios.get('/api/users/status');
                if (userStatus.data.loggedIn) {
                    const currentUser = userStatus.data.user;

                    if (currentUser.role === 'admin' || currentUser.role === 'director') {
                        const racesResponse = await axios.get(`/api/races/list`);
                        setRaces(racesResponse.data);

                        if (raceId && currentUser.role === 'director') {
                            const directorRaces = racesResponse.data.map(race => race.id.toString());
                            if (!directorRaces.includes(raceId)) {
                                setError('You are not authorized to generate a token for this race.');
                                setSelectedRace('');
                            }
                        }
                    } else {
                        setError('Forbidden: You do not have access to this page.');
                    }
                } else {
                    setError('You must be logged in to access this page.');
                }
            } catch (err) {
                setError('Error fetching data: ' + (err.response?.data || err.message));
            } finally {
                setLoading(false);
            }
        };

        fetchUserAndRaces();
    }, [raceId]);

    const generateToken = async () => {
        setError('');
        setToken('');

        if (!selectedRace) {
            setError('Please select a race.');
            return;
        }

        try {
            const response = await axios.post('/api/users/generate-role-token', {
                role: 'photographer',
                race_id: selectedRace,
            });
            setToken(`/register?roleToken=${response.data.token}`);
        } catch (err) {
            setError(err.response?.data || 'Error generating token.');
        }
    };

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Invite Photographer
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Race</InputLabel>
                    <Select
                        value={selectedRace}
                        label="Select Race"
                        onChange={(e) => setSelectedRace(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>-- Select a race --</em>
                        </MenuItem>
                        {races.map(race => (
                            <MenuItem key={race.id} value={race.id}>{race.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="contained" color="primary" onClick={generateToken} fullWidth>
                    Generate Invitation
                </Button>
                {token && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        <Typography>Generated Invitation Link:</Typography>
                        <pre>{token}</pre>
                    </Alert>
                )}
            </Paper>
        </Container>
    );
};

export default GeneratePhotographerTokenPage;
